/**
 * Data layer for `kpi-instructions`. Thin typed fetch wrappers over /api/v1/kpi-instructions.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface KpiInstructionsRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListKpiInstructionsResponse {
	count: number;
	items: KpiInstructionsRow[];
}

export interface CreateKpiInstructionsBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/kpi-instructions";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListKpiInstructionsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListKpiInstructionsResponse>(res);
}

export async function create(body: CreateKpiInstructionsBody): Promise<KpiInstructionsRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<KpiInstructionsRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const kpiInstructionsQueryKeys = {
	all: ["kpi-instructions"] as const,
	list: () => [...kpiInstructionsQueryKeys.all] as const,
	detail: (id: string) => [...kpiInstructionsQueryKeys.all, id] as const,
};
